import { Menu as MenuIcon, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/cn";
import { Sidebar } from "./Sidebar";

type AppShellProps = {
  sidebarFooter?: ReactNode;
  children: ReactNode;
  className?: string;
};

/**
 * Global app layout — sidebar on the left, scrolling main area on the right.
 * Outlet pages live in ``children``; route-level data fetching happens there.
 *
 * On mobile (<lg) the sidebar becomes a slide-over drawer behind a
 * hamburger button. The desktop 244px grid column collapses to 0 below
 * lg so the main pane takes the full width.
 */
export function AppShell({ sidebarFooter, children, className }: AppShellProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Auto-close the mobile drawer on route change.
  const closeMenu = () => setMenuOpen(false);
  void location; // closeMenu is called explicitly; this keeps the hook wired

  return (
    <div
      className={cn(
        "grid min-h-screen grid-cols-1 lg:grid-cols-[244px_1fr]",
        className,
      )}
    >
      {/* Mobile top bar with hamburger — hidden on lg+ */}
      <div className="flex items-center justify-between border-b border-stroke bg-bg-raised px-4 py-2.5 lg:hidden">
        <div className="font-serif text-sm font-semibold">Rentier 2050</div>
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Otevřít menu"
          className="rounded-lg p-1.5 text-ink hover:bg-surface"
        >
          <MenuIcon size={20} />
        </button>
      </div>

      {/* Drawer overlay (mobile) */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — persistent on desktop, drawer on mobile */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-[260px] transition-transform lg:static lg:translate-x-0",
          menuOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {/* Mobile close button */}
        <button
          type="button"
          onClick={closeMenu}
          aria-label="Zavřít menu"
          className="absolute right-2 top-2 z-10 rounded-lg p-1.5 text-ink-dim hover:bg-surface hover:text-ink lg:hidden"
        >
          <X size={18} />
        </button>
        <Sidebar footer={sidebarFooter} onNavigate={closeMenu} />
      </div>

      <main className="w-full max-w-[1460px] px-4 py-6 pb-12 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  );
}
